import { FormEvent, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import classnames from 'classnames';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Patient } from 'types';
import { FormErrorHelperText, FormInput } from 'components';
import { LanguageSelector } from 'components/LanguageSelector/LanguageSelector';
import { UserIcon } from 'assets';

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

import styles from './WelcomeDialog.module.scss';

export interface WelcomeDialogProps extends DialogProps {
  onConfirm: (patient: Patient) => void;
  avatarUrl?: string;
  profileFullName: string;
  organizationName: string;
  isLoading?: boolean;
  requirePhoneNumber?: boolean;
}

export function WelcomeDialog({
  onConfirm,
  avatarUrl,
  profileFullName,
  organizationName,
  isLoading = false,
  requirePhoneNumber = false,
  ...rest
}: WelcomeDialogProps) {
  const { t, i18n } = useTranslation('link');
  const isRtl = i18n.dir() === 'rtl';
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const schema = Yup.object().shape({
    userName: Yup.string()
      .required(t('welcomeDialog.validation.nameRequired'))
      .min(2, t('welcomeDialog.validation.minLength2'))
      .max(40, t('welcomeDialog.validation.maxLength40')),
    phoneNumber: requirePhoneNumber
      ? Yup.string()
          .required(t('welcomeDialog.validation.phoneRequired'))
          .test('phone-digits', t('welcomeDialog.validation.phoneInvalid'), (v) => v?.replace(/\D/g, '').length === 10)
      : Yup.string().nullable(),
  });

  const welcomeDialogInputSx = {
    backgroundColor: 'var(--app-field-surface) !important',
    color: 'var(--app-text-default) !important',
    '&:hover': {
      backgroundColor: 'var(--app-field-surface) !important',
    },
    '&.Mui-focused': {
      backgroundColor: 'var(--app-field-surface) !important',
    },
    '&.Mui-disabled': {
      backgroundColor: 'var(--app-field-surface-disabled) !important',
    },
    '& .MuiInputBase-input': {
      backgroundColor: 'var(--app-field-surface) !important',
      color: 'var(--app-text-default) !important',
      caretColor: 'auto',
      '&::placeholder': {
        color: 'var(--app-text-muted)',
        opacity: 1,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--app-brand-primary) !important',
      borderWidth: '1.5px !important',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--app-brand-primary) !important',
      borderWidth: '2px !important',
    },
    boxShadow: '0 0 0 3px color-mix(in srgb, var(--app-brand-primary) 20%, transparent)',
  } as const;

  const form = useForm<Patient>({
    resolver: yupResolver(schema),
    defaultValues: {
      userName: '',
      phoneNumber: undefined,
    },
    mode: 'all',
  });
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const languageSelectorWidth = 52;
  const showOrganizationFallback = !avatarUrl || avatarLoadFailed;

  useEffect(() => {
    // Ensure dialog has mounted before focusing
    setTimeout(() => {
      form.setFocus('userName');
    }, 0);
  }, [form]);

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();

    if (form.formState.isValid) {
      const formValues = form.getValues();
      formValues.id = uuidv4();
      onConfirm(formValues);
    }
  }

  return (
    <Dialog {...rest} className={classnames(styles.dialog, { [styles.dialogMobile]: isMobile })}>
      <DialogTitle>
        {!showOrganizationFallback ? (
          <Avatar
            className={classnames(styles.avatar, {
              [styles.avatarTablet]: isTablet,
              [styles.avatarMobile]: isMobile,
            })}
            variant='square'
            src={avatarUrl}
            imgProps={{
              onError: () => setAvatarLoadFailed(true),
            }}
          >
            {organizationName}
          </Avatar>
        ) : (
          <Box
            className={classnames(styles.organizationPanel, {
              [styles.organizationPanelTablet]: isTablet,
              [styles.organizationPanelMobile]: isMobile,
            })}
          >
            <Box className={styles.organizationIconCircle}>
              <UserIcon sx={{ color: 'var(--app-welcome-panel-icon-fg)', fontSize: '7.6rem' }} />
            </Box>
            <Typography component='p' variant='h3' className={styles.waitingRoomTitle}>
              {t('welcomeDialog.waitingRoom')}
            </Typography>
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        <Box
          component='form'
          onSubmit={handleSubmit}
          className={classnames(styles.form, { [styles.formMobile]: isMobile })}
        >
          <Typography component='p' variant='h3' className={styles.welcome}>
            {t('welcomeDialog.welcomeTo')}
            <br />
            {organizationName}
          </Typography>

          <Box className={styles.hostRow}>
            <Typography component='p' variant={isTablet ? 'body2' : 'body5'} className={styles.hostName}>
              {profileFullName}
            </Typography>
            <LanguageSelector
              width={languageSelectorWidth}
              compact
              compactMenuWidth={115}
              menuAlign={isRtl ? 'center' : 'right'}
              denseMenuItems
              sx={{
                '& .MuiButton-startIcon': {
                  marginLeft: 0,
                  marginRight: 0,
                  marginInlineStart: '2px',
                  marginInlineEnd: '4px',
                },
                '& .MuiButton-endIcon': {
                  marginLeft: 0,
                  marginRight: 0,
                  marginInlineStart: '2px',
                  marginInlineEnd: 0,
                },
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                paddingInlineStart: '4px !important',
                paddingInlineEnd: '6px !important',
              }}
            />
          </Box>

          <Box position='relative'>
            <FormInput
              name='userName'
              control={form.control}
              placeholder={t('welcomeDialog.namePlaceholder')}
              className={styles.welcomeDialogInput}
              sx={welcomeDialogInputSx}
              inputProps={{
                style: {
                  backgroundColor: 'var(--app-field-surface)',
                  color: 'var(--app-text-default)',
                  caretColor: 'auto',
                },
              }}
              classes={{ input: styles.nameInput }}
              errorHelperClassName={styles.errorHelper}
            />
          </Box>
          {requirePhoneNumber && (
            <Box position='relative'>
              <Controller
                name='phoneNumber'
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <TextField
                      {...field}
                      fullWidth
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(formatPhoneNumber(e.target.value))}
                      placeholder={t('welcomeDialog.phoneOptionalPlaceholder')}
                      className={styles.welcomeDialogInput}
                      sx={welcomeDialogInputSx}
                      inputProps={{
                        style: {
                          backgroundColor: 'var(--app-field-surface)',
                          color: 'var(--app-text-default)',
                          caretColor: 'auto',
                        },
                      }}
                      error={fieldState.isTouched && !!fieldState.error}
                    />
                    <FormErrorHelperText className={styles.errorHelper}>
                      {fieldState.isTouched ? fieldState.error?.message : ''}
                    </FormErrorHelperText>
                  </>
                )}
              />
            </Box>
          )}

          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={!form.formState.isValid || isLoading}
            className={classnames(styles.formButton, {
              [styles.formButtonTablet]: isTablet,
            })}
            type='submit'
          >
            {isLoading ? (
              <CircularProgress size={20} thickness={5} sx={{ color: 'inherit' }} />
            ) : (
              t('welcomeDialog.continue')
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
